/**
 * Product Item Drawer
 *
 * Slide-in drawer for a product item. Mirrors the task drawer: inline-editable
 * title/description (double-click, no modal), an editable priority badge,
 * account-linked customers, sectioned content (feature flags, comments) and a
 * solid footer with stage-transition actions + delete.
 *
 * @module features/product-board/components/ProductItemDrawer
 */

import React from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerFooter,
  DrawerCloseButton,
  Flex,
  Text,
  Button,
  TagBadge,
  Markdown,
  SplitButton,
  Dialog,
  DialogTrigger,
  DialogFooter,
  useToast,
  InlineEditableText,
} from '@chatsdet/ui';
import {
  Trash,
  ArrowCounterClockwise,
  Archive,
  Flag,
  PauseCircle,
  PlayCircle,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { boardHasFeature } from '@chatsdet/cards-plugins';
import {
  useProductItems,
  useUpdateProductItem,
  useMoveProductItem,
  useDeleteProductItem,
} from '../hooks/useProductItems';
import { STAGE_LABELS } from '../constants';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { SectionLabel } from '../../cards/components/SectionLabel';
import { CardCommentsPanel } from '../../cards/components/CardCommentsPanel';
import { AddProductItemCustomerButton } from './AddProductItemCustomerButton';
import { ProductCustomerBadge } from './ProductCustomerBadge';
import { ProductPriorityBadge } from './ProductPriorityBadge';
import { ProductItemFlagsSection } from './ProductItemFlagsSection';
import { ProductItemBakePlansSection } from './ProductItemBakePlansSection';
import { ProductItemMetricsSection } from './ProductItemMetricsSection';
import { ProductItemPageSkillsAnalysisSection } from './ProductItemPageSkillsAnalysisSection';
import { ProductItemPageSkillsExperimentSection } from './ProductItemPageSkillsExperimentSection';

interface Props {
  itemId: string;
  includeDeprecated: boolean;
  onClose: () => void;
}

export function ProductItemDrawer({ itemId, includeDeprecated, onClose }: Props) {
  // Fetch unfiltered (just the deprecated toggle) so the drawer always resolves
  // the item even when the board is narrowed by priority/customer filters.
  const { data: items } = useProductItems(includeDeprecated);
  const item = items?.find((it) => it.id === itemId) ?? null;

  const updateItem = useUpdateProductItem(includeDeprecated);
  const moveItem = useMoveProductItem(includeDeprecated);
  const deleteItem = useDeleteProductItem(includeDeprecated);
  const { toast } = useToast();

  const membersQuery = useOrgMembers();
  const members = membersQuery.data?.items ?? [];

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  if (!item) {
    return (
      <Drawer>
        <Flex justify="center" align="center" className="p-8">
          <Text>Item not found</Text>
        </Flex>
      </Drawer>
    );
  }

  const isDeprecated = item.stage === 'deprecated';
  const isOnHold = item.stage === 'on_hold';
  const customers = item.customers ?? [];
  const stageColor: 'red' | 'gray' | 'blue' = isDeprecated ? 'red' : isOnHold ? 'gray' : 'blue';

  const resolveActorName = (actor: string | null): string => {
    if (!actor) return 'system';
    const member = members.find((m) => m.userId === actor);
    if (member) return member.displayName || member.primaryEmail || actor;
    return actor.slice(0, 8);
  };
  const submitterName = item.createdBy ? resolveActorName(item.createdBy) : null;

  const moveToStage = (
    stage: 'deprecated' | 'on_hold' | 'beta' | 'ideas',
    successMessage: string,
    errorMessage: string
  ) =>
    moveItem.mutate(
      { id: item.id, stage, beforeItemId: null, afterItemId: null },
      {
        onSuccess: () => {
          toast.success(successMessage);
          onClose();
        },
        onError: () => toast.error(errorMessage),
      }
    );

  const handleConfirmDelete = async () => {
    try {
      await deleteItem.mutateAsync(item.id);
      toast.success('Deleted');
      setDeleteConfirmOpen(false);
      onClose();
    } catch {
      toast.error('Delete failed');
    }
  };

  // The footer's primary action is the most logical next stage transition for
  // the item's current state; the rest live in the dropdown.
  const primaryStageAction = isDeprecated
    ? {
        label: 'Restore to Ideas',
        icon: <ArrowCounterClockwise size={14} />,
        run: () => moveToStage('ideas', 'Restored to Ideas', 'Failed to restore'),
      }
    : isOnHold
      ? {
          label: 'Resume to Beta',
          icon: <PlayCircle size={14} />,
          run: () => moveToStage('beta', 'Moved to Beta', 'Failed to resume item'),
        }
      : {
          label: 'Put on Hold',
          icon: <PauseCircle size={14} />,
          run: () => moveToStage('on_hold', 'Moved to On Hold', 'Failed to move item'),
        };

  return (
    <Drawer hideCloseButton>
      <DrawerHeader className="space-y-3">
        {/* Row 1: stage + priority + close */}
        <Flex align="center" justify="between" gap="md">
          <Flex align="center" gap="sm" wrap>
            <TagBadge name={STAGE_LABELS[item.stage]} color={stageColor} hideDot size="sm" />
            <ProductPriorityBadge item={item} includeDeprecated={includeDeprecated} size="sm" />
          </Flex>
          <DrawerCloseButton />
        </Flex>

        {/* Title — double-click to edit inline */}
        <InlineEditableText
          value={item.title}
          onSave={(title) => updateItem.mutate({ id: item.id, title })}
          ariaLabel="Product item title"
          placeholder="Untitled item"
          className="text-xl font-semibold leading-tight"
          inputClassName="text-xl font-semibold leading-tight"
        />

        {/* Metadata */}
        <Flex align="center" gap="xs" wrap className="min-w-0">
          <Text size="sm" className="text-muted-foreground">
            Updated {formatDistanceToNow(new Date(item.updatedAt))} ago
          </Text>
          {submitterName && (
            <Text size="sm" className="text-muted-foreground">
              · created by <span className="text-foreground">{submitterName}</span>
            </Text>
          )}
        </Flex>

        {/* Customers */}
        <Flex gap="xs" wrap align="center">
          <Text size="sm" className="text-muted-foreground">
            Customers
          </Text>
          {customers.map((customer) => (
            <ProductCustomerBadge
              key={customer.id}
              productItemId={item.id}
              customer={customer}
              canEdit
            />
          ))}
          <AddProductItemCustomerButton productItemId={item.id} />
        </Flex>
      </DrawerHeader>

      <DrawerContent className="space-y-7">
        {/* Description — clean inline body text, double-click to edit */}
        <InlineEditableText
          value={item.description ?? ''}
          multiline
          onSave={(description) => updateItem.mutate({ id: item.id, description })}
          ariaLabel="Product item description"
          placeholder="Add a description…"
          className="text-[15px] leading-relaxed text-foreground"
          inputClassName="min-h-[320px] text-[15px] leading-relaxed"
          renderDisplay={(value) => (
            <Markdown
              compact
              className="text-[15px] leading-relaxed [&_img]:max-w-full [&_img]:rounded-md [&_img]:my-2"
            >
              {value}
            </Markdown>
          )}
        />

        <ProductItemFlagsSection productItemId={item.id} />

        <ProductItemBakePlansSection productItemId={item.id} />

        <ProductItemMetricsSection productItemId={item.id} />

        <ProductItemPageSkillsAnalysisSection productItemId={item.id} />

        <ProductItemPageSkillsExperimentSection productItemId={item.id} />

        {boardHasFeature('product_item', 'comments') && (
          <section className="space-y-3 border-t border-border pt-6">
            <SectionLabel>Comments</SectionLabel>
            <CardCommentsPanel cardId={item.id} className="space-y-4" />
          </section>
        )}
      </DrawerContent>

      <DrawerFooter className="shadow-[0_-2px_12px_-6px_rgba(0,0,0,0.12)]">
        <Flex align="center" justify="between" gap="md" className="w-full">
          <Text size="xs" className="text-muted-foreground">
            {STAGE_LABELS[item.stage]}
          </Text>
          {item.flagDriven ? (
            // Stage is derived from the linked feature flags' rollout state —
            // manual stage transitions are blocked (the API rejects them too).
            <Flex align="center" gap="sm">
              <Flex align="center" gap="xs" className="text-muted-foreground">
                <Flag size={13} weight="fill" />
                <Text size="xs" className="text-muted-foreground">
                  Stage managed by feature flags
                </Text>
              </Flex>
              <Button
                variant="outline"
                size="sm"
                onPress={() => setDeleteConfirmOpen(true)}
                iconLeft={<Trash size={14} />}
              >
                Delete
              </Button>
            </Flex>
          ) : (
            <SplitButton
              variant="outline"
              size="sm"
              onPress={primaryStageAction.run}
              isDisabled={moveItem.isPending}
            >
              <Flex align="center" gap="xs">
                {primaryStageAction.icon}
                {primaryStageAction.label}
              </Flex>
              {!isDeprecated && (
                <SplitButton.Item
                  key="deprecate"
                  onAction={() =>
                    moveToStage('deprecated', 'Moved to Deprecated', 'Failed to deprecate')
                  }
                >
                  <Flex align="center" gap="xs">
                    <Archive size={14} />
                    Deprecate
                  </Flex>
                </SplitButton.Item>
              )}
              {!isDeprecated && <SplitButton.Separator />}
              <SplitButton.Item
                key="delete"
                destructive
                onAction={() => setDeleteConfirmOpen(true)}
              >
                <Flex align="center" gap="xs">
                  <Trash size={14} />
                  Delete permanently
                </Flex>
              </SplitButton.Item>
            </SplitButton>
          )}
        </Flex>
      </DrawerFooter>

      <DialogTrigger isOpen={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <span style={{ display: 'none' }} />
        <Dialog title="Delete product item">
          <Text>
            Permanently delete <strong>{item.title}</strong>? This cannot be undone.
          </Text>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                iconLeft={<Trash size={14} weight="bold" />}
                onPress={handleConfirmDelete}
                isDisabled={deleteItem.isPending}
              >
                {deleteItem.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Drawer>
  );
}
